import { LuPlus } from "react-icons/lu";
import { backgroundColors } from "../../helper/constant";
import { GoDotFill } from "react-icons/go";
import { useCallback, useEffect, useState, useRef } from "react";
import CreateClassmodel from "../model/CreateClassmodel";
import { useDispatch } from "react-redux";
import { setBgcolor, setClassData } from "../../redux/slices/classdetailSlice";
import { Link } from "react-router-dom";
import { setTabData } from "../../redux/slices/classtab";
import { useTranslation } from "react-i18next";
import { ApiPost, ApiDelete, ApiPut } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import ClasscardSkeleton from "../skeleton/ClasscardSkeleton";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { ImSpinner8 } from "react-icons/im";
import { removeAlertPopup } from "../../helper/commonFunctions";

export default function Class() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classColors, setClassColors] = useState({});
  const [lastId, setLastId] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [apiError, setApiError] = useState(false);
  const observer = useRef(null);
  const userData = JSON.parse(localStorage?.getItem("zubrus_user_data"))?.data?.userInfo;
  console.log('userData :>> ', userData);
  const getRandomColor = () => {
    return backgroundColors[Math.floor(Math.random() * backgroundColors.length)];
  };

  const getClasses = useCallback(async (reset = false) => {
    if (!hasMore && !reset) return;
    if (apiError) return;

    setLoading(true);
    try {
      const url = reset || !lastId
        ? `/contents/classes/search?pageSize=10`
        : `/contents/classes/search?lastId=${lastId}&pageSize=10`;

      const response = await ApiPost(url, {
        "country": "LT"  
      });
      const classesData = (response?.data || []).filter(item =>
        item.status !== "DELETED" && item.classId !== null
      );

      if (reset) {
        setData(classesData);
        setLastId(classesData.length > 0 ? classesData[classesData.length - 1]._id : "");
        setHasMore(classesData.length > 0);
      } else {
        if (classesData.length === 0) {
          setHasMore(false);
        } else {
          setData(prevData => [...prevData, ...classesData]);
          setLastId(classesData[classesData.length - 1]._id);
        }
      }

      // Generate and store random colors for each class
      const colors = {};
      classesData.forEach(classItem => {
        if (!classColors[classItem.classId]) {
          colors[classItem.classId] = getRandomColor();
        }
      });
      setClassColors(prevColors => ({ ...prevColors, ...colors }));
      setApiError(false);
    } catch (error) {
      setApiError(true);
      setHasMore(false);
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setLoading(false);
    }
  }, [lastId, apiError]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getClasses(true);
  }, []);

  //for pagination
  useEffect(() => {
    if (loading || !hasMore || apiError) return;

    const currentObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          getClasses();
        }
      },
      { threshold: 1.0 }
    );

    const loadMoreTrigger = document.getElementById("loadMoreTrigger");
    if (loadMoreTrigger) {
      currentObserver.observe(loadMoreTrigger);
    }

    return () => currentObserver.disconnect();
  }, [loading, hasMore, apiError, getClasses]);

  const handleDeleteClass = (classId, className) => {
    removeAlertPopup({
      t,
      preConfirm: async () => {
        await ApiDelete(`/contents/classes/${classId}`)
        .then(() => {
          setData(prevData => prevData.filter(item => item.classId !== classId));
          commonToast(t("classDeletedSuccess"), "success");
        }).catch((error) => {
          commonToast(error?.data?.error || error?.message, "error");
        })
      },
      name: className,
    })
  };

  const handleEditClass = (e, classData) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingClass(classData);
    setEditMode(true);
    setOpen(true);
  };

  const handleUpdateClass = async (updatedData) => {
    try {
      await ApiPut('/contents/classes', {
        className: updatedData.className,
        smallLogoUrl: updatedData.smallLogoUrl,
        largeLogoUrl: updatedData.largeLogoUrl,
        classId: updatedData.classId,
        "schoolId": "68753cba43fc9b267583809a",    
        "country": "LT"
      });
      commonToast(t("classUpdatedSuccess"), "success");
      getClasses(true);
      setEditMode(false);
      setEditingClass(null);
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, "error");
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-10 px-5">
      <div className="flex justify-between items-center">
        <div>
          <span className="text-2xl font-semibold">{t("myclasses")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="regular-text">{t("createclass")}</span>
          <div
            className="border rounded-full cursor-pointer size-8 flex justify-center items-center"
            onClick={() => {
              setEditMode(false);
              setEditingClass(null);
              setOpen(true);
            }}
          >
            <LuPlus fontSize={24} />
          </div>
        </div>
      </div>
      {loading && data.length === 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-1 gap-10 my-5">
          <ClasscardSkeleton />
        </div>
      ) : data?.length ? (
        <div className="grid grid-cols-2 md:grid-cols-1 gap-10 my-5">
          {data.map((item, key) => (
            <div className="relative group">
              {(userData?.userId === item?.createdBy) &&
                <div className="absolute top-4 right-4 z-10 flex gap-3">
                  <button
                    onClick={(e) => handleEditClass(e, item)}
                    className="p-2 rounded-lg bg-blue-100/50 border border-blue-500/20 backdrop-blur-sm"
                    aria-label="Edit class"
                  >
                    <FiEdit className="text-blue-600 size-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClass(item.classId, item?.className)
                    }}
                    className="p-2 rounded-lg bg-red-100/50 border border-red-500/20 backdrop-blur-sm"
                    aria-label="Delete class"
                  >
                    <FiTrash2
                      className="text-red-600 size-4"
                    />
                  </button>
                </div>}
              <Link
                key={key}
                to={`/classes/${item?.classId}`}
                onClick={() => {
                  dispatch(setClassData(item));
                  dispatch(setBgcolor(classColors[item.classId] || getRandomColor()));
                  dispatch(setTabData("overview"));
                }}
                className="h-[220px] cursor-pointer px-5 rounded-3xl flex justify-center items-center gap-10 hover:shadow-lg transition-shadow"
                style={{ backgroundColor: classColors[item.classId] || getRandomColor() }}
              >
                <div>
                  <img
                    src={item?.largeLogoUrl}
                    alt={item?.className}
                    className="size-[120px] object-contain object-center"
                  />
                </div>
                <div>
                  <h2 className="medium-text">{item?.className}</h2>
                  <div className="text-sm text-lightgray flex gap-2 items-center mt-3">
                    <h6 className="text-center">
                      {t("student")} :  {item?.students?.length || 0}
                    </h6>
                    <GoDotFill />
                    <h6 className="text-center">
                      {t("teacher")} :  {item?.otherTeachers?.length + 1 || 0}
                    </h6>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="h-[145px] mt-20 shadow-inner-top-strong rounded-t-3xl px-10 flex items-end justify-center">
          <span className="regular-text text-lightgray">
            {t("createfirstclass")}
          </span>
        </div>
      )}
      {loading && data.length > 0 && <div className="text-center my-4"><ImSpinner8 className="animate-spin text-darkblue size-8 mx-auto" /></div>}
      {apiError && (
        <div className="text-center mt-20">
          <p className="text-2xl text-red-500">Error loading data</p>
          <button
            onClick={() => {
              setApiError(false);
              getClasses(true);
            }}
            className="mt-4 px-4 py-2 bg-darkblue text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}
      {hasMore && !apiError && <div id="loadMoreTrigger" className="h-10"></div>}
      <CreateClassmodel
        open={open}
        setOpen={setOpen}
        getClasses={getClasses}
        editMode={editMode}
        editingClass={editingClass}
        onUpdate={handleUpdateClass}
      />
    </div>
  );
}
